import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchCommunityTopicsThunk, fetchTrendingTopicsThunk } from '../../redux/communitySlice';
import type { CommunityLayoutOutletContext } from '../layout/CommunityLayout';
import './TopicExplorerPage.css';

type TopicCategory = 'all' | 'seasonal' | 'constitution' | 'therapy' | 'case';

const CATEGORY_OPTIONS: Array<{ value: TopicCategory; label: string; keywords?: string[] }> = [
  { value: 'all', label: '全部话题' },
  { value: 'seasonal', label: '节气调养', keywords: ['节气', '春', '夏', '秋', '冬', '养生', '时令'] },
  { value: 'constitution', label: '体质调护', keywords: ['体质', '平和', '湿', '阳虚', '阴虚', '气虚'] },
  { value: 'therapy', label: '经典疗法', keywords: ['方', '针', '灸', '处方', '膏方'] },
  { value: 'case', label: '案例分享', keywords: ['案例', '经验', '调理', '康复'] },
];

const TopicExplorerPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { topics, topicsStatus, trendingTopics, trendingStatus } = useAppSelector((state) => state.community);
  const { searchInput, setSearchInput } = useOutletContext<CommunityLayoutOutletContext>();
  const [category, setCategory] = useState<TopicCategory>('all');

  useEffect(() => {
    if (topicsStatus === 'idle') {
      dispatch(fetchCommunityTopicsThunk());
    }
  }, [topicsStatus, dispatch]);

  useEffect(() => {
    if (trendingStatus === 'idle') {
      dispatch(fetchTrendingTopicsThunk());
    }
  }, [trendingStatus, dispatch]);

  const keyword = searchInput.trim();
  const filteredTopics = useMemo(() => {
    const normalized = keyword.toLowerCase();
    return topics.filter((topic) => {
      const label = topic.label.toLowerCase();
      const matchKeyword = normalized ? label.includes(normalized) : true;
      if (!matchKeyword) {
        return false;
      }
      if (category === 'all') {
        return true;
      }
      const tokens = CATEGORY_OPTIONS.find((option) => option.value === category)?.keywords ?? [];
      if (!tokens.length) {
        return true;
      }
      return tokens.some((token) => label.includes(token.toLowerCase()));
    });
  }, [topics, keyword, category]);

  const handleOpenTopic = (topicId: string) => {
  navigate(`/app/community/topics/${topicId}`);
  };

  const handleRefreshTopics = () => {
    dispatch(fetchCommunityTopicsThunk());
    dispatch(fetchTrendingTopicsThunk());
  };

  const handleResetSearch = () => {
    if (searchInput) {
      setSearchInput('');
    }
  };

  return (
    <div className="community-topics">
      <header className="community-topics__header">
        <div>
          <h1>话题广场</h1>
          <p>探索节气养生、体质调护与经典案例，快速跳转到垂直讨论区。</p>
        </div>
        <button type="button" className="community-topics__refresh" onClick={handleRefreshTopics}>
          刷新
        </button>
      </header>

      <section className="community-topics__filters">
        <div className="community-topics__chips" role="tablist">
          {CATEGORY_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              role="tab"
              aria-selected={category === option.value}
              className={`community-topics__chip${category === option.value ? ' community-topics__chip--active' : ''}`}
              onClick={() => setCategory(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
        <div className="community-topics__summary">
          <span>匹配话题：{filteredTopics.length}</span>
          {keyword && (
            <button type="button" onClick={handleResetSearch}>
              清除搜索
            </button>
          )}
        </div>
      </section>

      <section className="community-topics__trending">
        <header>
          <h2>实时热榜</h2>
          {trendingStatus === 'loading' && <span>加载中...</span>}
        </header>
        {trendingStatus === 'failed' ? (
          <div className="community-topics__placeholder">热门话题加载失败，可稍后重试。</div>
        ) : (
          <ul>
            {trendingTopics.slice(0, 8).map((topic) => (
              <li key={topic.id}>
                <button type="button" onClick={() => handleOpenTopic(topic.id)}>
                  #{topic.label}
                </button>
                <span>{topic.mentions} 条讨论</span>
              </li>
            ))}
            {trendingTopics.length === 0 && trendingStatus === 'succeeded' && (
              <li className="community-topics__placeholder">暂无热门数据，试着发起第一篇讨论。</li>
            )}
          </ul>
        )}
      </section>

      <div className="community-topics__grid">
        {topicsStatus === 'loading' && filteredTopics.length === 0 ? (
          <div className="community-topics__placeholder">话题列表加载中...</div>
        ) : filteredTopics.length === 0 ? (
          <div className="community-topics__placeholder">
            未找到匹配话题，可以尝试切换分类或调整搜索关键字。
          </div>
        ) : (
          filteredTopics.map((topic) => (
            <article key={topic.id} className="community-topics__card">
              <h2>#{topic.label}</h2>
              <p>进入话题专区，查看社区内的精选讨论与最新动态。</p>
              <div className="community-topics__card-actions">
                <button type="button" onClick={() => handleOpenTopic(topic.id)}>
                  查看讨论
                </button>
                <span>话题关注功能即将上线</span>
              </div>
            </article>
          ))
        )}
      </div>
    </div>
  );
};

export default TopicExplorerPage;
